import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, UserPlus, UserMinus, Users, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { StrokesToParBadge } from '../components/StrokesToParBadge';
import { TIER_STYLES, TierPill, getUserTier } from '../components/TierBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getDefaultPhoto = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'G')}&background=0F3B2E&color=fff&size=400&font-size=0.4&bold=true`;
const getPhotoUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [dmLoading, setDmLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/profile/${userId}`);
        setUser(res.data);
      } catch {
        toast.error('User not found');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, navigate]);

  const toggleFollow = async () => {
    if (!user) return;
    setFollowLoading(true);
    try {
      if (user.is_following) {
        await axios.delete(`${API}/follow/${user.id}`);
        setUser(prev => ({ ...prev, is_following: false, followers_count: (prev.followers_count || 1) - 1 }));
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API}/follow/${user.id}`);
        setUser(prev => ({ ...prev, is_following: true, followers_count: (prev.followers_count || 0) + 1 }));
        toast.success('Following!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setFollowLoading(false);
    }
  };

  const startDM = async () => {
    setDmLoading(true);
    try {
      const res = await axios.post(`${API}/conversations/start`, { user_id: userId });
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start conversation');
    } finally {
      setDmLoading(false);
    }
  };

  const paceLabels = { ready_golf: 'Ready Golf', quick: 'Quick', average: 'Average', deliberate: 'Deliberate' };
  const rideLabels = { walk: 'Walker', ride: 'Rider', either: 'Either' };

  if (loading) return (
    <div className="min-h-screen bg-background pb-24">
      <div className="animate-pulse max-w-lg mx-auto px-4 pt-16">
        <div className="w-24 h-24 rounded-full mx-auto bg-muted mb-4" />
        <div className="h-6 w-40 mx-auto rounded bg-muted mb-2" />
        <div className="h-4 w-32 mx-auto rounded bg-muted" />
      </div>
      <Navigation />
    </div>
  );

  if (!user) return null;

  const photos = user.photos?.filter(Boolean) || [];
  const mainPhoto = getPhotoUrl(user.profile_photo) || (photos.length > 0 ? getPhotoUrl(photos[0]) : getDefaultPhoto(user.display_name));

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="user-profile-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-xl">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-serif text-lg font-bold text-foreground">{user.display_name || user.username}</h1>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Profile Header */}
        <div className="text-center mb-6">
          <div className="relative w-24 h-24 mx-auto mb-3">
            {getUserTier(user) && (
              <div className="absolute -inset-[4px] rounded-full" style={{ background: TIER_STYLES[getUserTier(user)].ring }} data-testid="profile-tier-ring" />
            )}
            <Avatar className="w-24 h-24 border-4 border-primary/20 relative">
              <AvatarImage src={mainPhoto} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                {user.display_name?.charAt(0) || 'G'}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <h2 className="font-serif text-xl font-bold text-foreground">
              {user.display_name}{user.age ? `, ${user.age}` : ''}
            </h2>
            {getUserTier(user) && <TierPill tier={getUserTier(user)} size="sm" />}
          </div>
          {user.is_founder && (
            <p className="text-xs font-semibold mt-1" style={{ color: '#B8923F' }} data-testid="founder-subtitle">Founder of The Club · Member #{user.user_number || 1}</p>
          )}
          {user.location && (
            <p className="text-sm flex items-center justify-center gap-1 mt-1 text-muted-foreground">
              <MapPin className="w-3.5 h-3.5" /> {user.location}
            </p>
          )}

          {/* Follow stats */}
          <div className="flex items-center justify-center gap-6 mt-3">
            <div className="text-center">
              <p className="text-lg font-bold text-foreground">{user.followers_count || 0}</p>
              <p className="text-[10px] uppercase font-semibold text-muted-foreground">Followers</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground">{user.following_count || 0}</p>
              <p className="text-[10px] uppercase font-semibold text-muted-foreground">Following</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <Button
              onClick={startDM}
              disabled={dmLoading}
              variant="outline"
              className="rounded-full gap-2 px-6"
              data-testid="profile-message-btn"
            >
              <MessageCircle className="w-4 h-4" /> Message
            </Button>
            <Button
              onClick={toggleFollow}
              disabled={followLoading}
              variant={user.is_following ? "outline" : "default"}
              className="rounded-full gap-2 px-6"
              data-testid="profile-follow-btn"
            >
              {user.is_following ? (
                <><UserMinus className="w-4 h-4" /> Following</>
              ) : (
                <><UserPlus className="w-4 h-4" /> Follow</>
              )}
            </Button>
          </div>
        </div>

        {/* Bio */}
        {user.bio && (
          <div className="rounded-xl border border-border p-4 mb-4 bg-card">
            <p className="text-sm text-foreground leading-relaxed">{user.bio}</p>
          </div>
        )}

        {/* Golf Info */}
        <div className="rounded-xl border border-border p-4 mb-4 bg-card">
          <h3 className="text-xs font-bold uppercase mb-3 text-muted-foreground">Golf Info</h3>
          <div className="flex flex-wrap gap-2">
            {user.display_metric?.value != null ? (
              <StrokesToParBadge display_metric={user.display_metric} strokes_to_par={user.strokes_to_par} />
            ) : user.handicap != null && (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-primary/10 text-primary">
                {user.handicap_official ? 'Official' : 'Est.'} {user.handicap} Hdcp
              </span>
            )}
            {user.strokes_to_par?.rounds_total > 0 && user.strokes_to_par?.method === 'insufficient_data' && (
              <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-muted text-muted-foreground" title="Post at least 3 18-hole rounds to see your Strokes to Par">
                Strokes to Par: {3 - user.strokes_to_par.rounds_used} more rounds
              </span>
            )}
            {user.playing_pace && (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-muted text-muted-foreground">
                {paceLabels[user.playing_pace] || user.playing_pace}
              </span>
            )}
            {user.walking_or_riding && (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-muted text-muted-foreground">
                {rideLabels[user.walking_or_riding] || user.walking_or_riding}
              </span>
            )}
            {user.is_private_club_member && (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-secondary/15 text-secondary">
                Private Club{user.private_club_name ? `: ${user.private_club_name}` : ''}
              </span>
            )}
            {user.plays_music && <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-muted text-muted-foreground">Music on Course</span>}
            {user.drinks_on_course === 'yes' && <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-muted text-muted-foreground">Drinks</span>}
            {user.plays_for_money && <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-secondary/15 text-secondary">Plays for $</span>}
          </div>
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="rounded-xl border border-border p-4 bg-card">
            <h3 className="text-xs font-bold uppercase mb-3 text-muted-foreground">Photos</h3>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <img key={i} src={getPhotoUrl(p)} alt="" className="w-full aspect-square rounded-lg object-cover" />
              ))}
            </div>
          </div>
        )}
      </div>

      <Navigation />
    </div>
  );
};

export default UserProfile;
